import React from "react";
import { Box, Typography, Stack, keyframes, Button } from "@mui/material";
// Import the custom Helmet component
import SeoHelmet from "../../../Components/SeoHelmet";
import ProjectCard from "./ProjectCard";
import { useGetProjectsQuery } from "../../../Store/api/rtk";
import { useNavigate } from "react-router-dom";

// Vertical infinite marquee scrolling up
const scrollUp = keyframes`
  0% { transform: translateY(0); }
  100% { transform: translateY(-50%); }
`;

// Vertical infinite marquee scrolling down
const scrollDown = keyframes`
  0% { transform: translateY(-50%); }
  100% { transform: translateY(0); }
`;

export default function Projects({}) {
  const navigate = useNavigate();
  const { data } = useGetProjectsQuery();
  const projectsData = data?.data?.d || [];

  // Reusable Marquee Column component inside the main container
  const MarqueeColumn = ({ direction = "up", speed = "40s", items = [] }) => (
    <Box
      sx={{
        flex: 1,
        height: "100%",
        overflow: "visible",
        position: "relative",
      }}
    >
      <Box
        className="marquee-track"
        sx={{
          display: "flex",
          flexDirection: "column",
          animation: `${direction === "up" ? scrollUp : scrollDown} ${speed} linear infinite`,
          willChange: "transform",
        }}
      >
        {/* Track duplicate container A for seamless infinite looping */}
        <Box>
          {items?.map((p, i) => (
            <ProjectCard key={`track-a-${p?.id || i}`} project={p} />
          ))}
        </Box>
        {/* Track duplicate container B for seamless infinite looping */}
        <Box>
          {items?.map((p, i) => (
            <ProjectCard key={`track-b-${p?.id || i}`} project={p} />
          ))}
        </Box>
      </Box>
    </Box>
  );

  // Safe fallback array manipulation using optional chaining and boundaries
  const reversedData =
    projectsData?.length > 0 ? [...projectsData]?.reverse() : [];
  const slicedData =
    projectsData?.length > 0
      ? [...projectsData, ...projectsData]?.slice(2)
      : [];

  return (
    <>
      {/* Vira SEO Helmet configured for the Portfolio / Projects page */}
      <SeoHelmet
        title="افتخارات و پروژه‌های ویرا | Vira-tec Portfolio"
        metaDescription="مجموعه‌ای از برترین پروژه‌های آژانس ویرا (Vira-tec) در توسعه وب‌سایت‌های مقیاس‌پذیر، پلتفرم‌های سازمانی، طراحی رابط کاربری لوکس و زیرساخت‌های شبکه‌ای."
        metaKeywords="نمونه‌کارهای ویرا, پروژه‌های vira-tec, پورتفولیو شرکت نرم افزاری, توسعه وب, طراحی ui ux, پروژه‌های میکروتیک"
      />

      <Box
        sx={{
          position: "relative",
          height: "100vh",
          bgcolor: "#020202",
          overflow: "hidden",
          "& *": { direction: "rtl" },
        }}
      >
        {/* Top ambient dark fading overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "15vh",
            background:
              "linear-gradient(to bottom, #020202 0%, transparent 100%)",
            zIndex: 5,
            pointerEvents: "none",
          }}
        />

        {/* Bottom ambient dark fading overlay */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "15vh",
            background: "linear-gradient(to top, #020202 0%, transparent 100%)",
            zIndex: 5,
            pointerEvents: "none",
          }}
        />

        {/* Marquee columns parent grid wrapper */}
        <Stack
          direction="row"
          gap={{ xs: 2, md: 3 }}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "200vh",
            px: { xs: 2, md: 3 },
            pt: { xs: 2, md: 3 },
            opacity: 0.8,
            zIndex: 1,
            "&:hover .marquee-track": {
              animationPlayState: "paused",
            },
          }}
        >
          {/* Column 1: Visible everywhere - Scrolls Up */}
          <MarqueeColumn direction="up" speed="55s" items={reversedData} />

          {/* Column 2: Visible everywhere - Scrolls Down */}
          <MarqueeColumn direction="down" speed="45s" items={projectsData} />

          {/* Column 3: Large screens only - Scrolls Up */}
          <Box sx={{ display: { xs: "none", lg: "block" }, flex: 1 }}>
            <MarqueeColumn direction="up" speed="65s" items={slicedData} />
          </Box>

          {/* Column 4: Ultra large screens only (xxl custom breakpoint match) - Scrolls Down */}
          <Box sx={{ display: { xs: "none", xxl: "block" }, flex: 1 }}>
            <MarqueeColumn direction="down" speed="50s" items={reversedData} />
          </Box>
        </Stack>

        {/* Premium frosted-glass frosted blur container in absolute center */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 10,
            bgcolor: "rgba(10, 10, 10, 0.65)",
            backdropFilter: "blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "32px",
            p: { xs: 4, md: 8 },
            textAlign: "center",
            boxShadow: "0 30px 60px rgba(0,0,0,0.8)",
            width: { xs: "90%", md: "auto" },
            minWidth: { md: "500px" },
            pointerEvents: "auto",
            transition: "transform 0.4s ease, border-color 0.4s ease",
            "&:hover": {
              borderColor: "rgba(0, 255, 204, 0.4)",
              transform: "translate(-50%, -50%) scale(1.02)",
            },
          }}
        >
          <Typography
            sx={{
              color: "#00ffcc",
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: "4px",
              textTransform: "uppercase",
              mb: 2,
            }}
          >
            Our Universe
          </Typography>
          <Typography
            component={"h1"}
            sx={{
              color: "#fff",
              fontSize: { xs: 30, md: 40 },
              fontWeight: 800,
              lineHeight: 1.1,
              letterSpacing: "-2px",
              mb: 2,
            }}
          >
            افتخارات ویرا
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 16 }}>
            برای مشاهده جزئیات هر پروژه روی آن توقف کنید.
          </Typography>
          <Button
            variant="outlined"
            sx={{ mt: "1.5rem" }}
            color="secondary"
            onClick={() => navigate("/projects-list")}
          >
            لیست کامل پروژه ها
          </Button>
        </Box>
      </Box>
    </>
  );
}
